/**
 * Created by Ning Jiang on 2021-05-03.
 */
import {NolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/NolimitAudioPlayer";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {IFadeTrackConf, IPlayTrackConf} from "@nolimitcity/slot-audio/bin/INolimitAudioPlayer";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {gsap} from "gsap";
import {NolimitHowl} from "@nolimitcity/slot-audio/bin/NolimitHowl";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {LoopableList} from "../../core/utils/LoopableList";

export class BlitzKriegSound {

    public static REELS_SPINNING: string = "reelsSpinning";
    public static REELS_LANDING: LoopableList<string> = new LoopableList<string>(["reelLanding01", "reelLanding02", "reelLanding03", "reelLanding04", "reelLanding05"]).setRandomizeAtWrapAround(true);
    public static SCATTER_ANTICIPATION_LANDING: LoopableList<string> = new LoopableList<string>(["scatterAnticipationLand01", "scatterAnticipationLand02", "scatterAnticipationLand03"]).setRandomizeAtWrapAround(true);
    public static SCATTER_LAND: string[] = ["scatter1Land", "scatter2Land", "scatter3Land", "scatter4Land", "scatter5Land"];
    public static FS_INTRO_GENERAL: string[] = ["fsIntroSelect1", "fsIntroSelect2", "fsIntroSelect3", "fsIntroSelect5", "fsIntroSelect4"];
    public static FS_INTRO_HIGHLIGHT: LoopableList<string> = new LoopableList<string>(["fsIntroHighlight01", "fsIntroHighlight02", "fsIntroHighlight03"]).setRandomizeAtWrapAround(true);
    public static BOMBER_WILD_EXPLODE: LoopableList<string> = new LoopableList<string>(["featureSymToWild01", "featureSymToWild02", "featureSymToWild03", "featureSymToWild04"]).setRandomizeAtWrapAround(true);
    public static MAIN_GAME_MUSIC: string = "main";
    public static FREESPIN_MUSIC: string = "fs";
    public static INTRO_LOOP: string = "introLoop";
    public static BIG_WIN_INTRO: string = "bigwinIntro";
    public static BIG_WIN_INTRO_BIG: string = "bigwinIntroBig";
    public static BIG_WIN_END: string = "bigwinEnd";
    public static WIN_CAP_START: string = "capwinStart";
    public static WIN_CAP_LOOP: string = "capwinLoop";
    public static WIN_CAP_END: string = "capwinEnd";
    public static TINY_WIN: string = "winTiny";
    public static AS_WIN: string = "asWin";
    public static AS_WIN_BIG: string = "asBigWin";
    public static SMALL_WIN: string = "winSmall";
    public static MEDIUM_WIN: string = "winMid";
    public static LARGE_WIN: string = "winLarge";
    public static WIN_COUNT_UP: string = "winCountup";
    public static WIN_FALL_BACK: string = "winFallback";
    public static REEL_OPEN: string = "reelOpen";
    public static WILD_LAND: string = "wildLand"
    public static WILD_MULTIPLIED: string = "wildMultiplied";
    public static xBetOn: string = "xbetOn";
    public static xBetOff: string = "xbetOff";
    public static FS_START: string = "fsStart";
    public static FS_END: string = "fsEnd";
    public static FS_END_OUTRO: string = "fsOutroEnd";
    public static WAYS_ELECTRICITY: string = "waysElectricity";
    public static EASTER_EGG: string = "easteregg";
    public static FEATURE_BOMBER_L2_READY: string = "featureBomberL2Ready";
    public static FEATURE_BOMBER_L2_FIRE: string = "featureBomberL2Fire";
    public static FEATURE_BOMBER_EXPLODE: string = "featureBomberExplode";
    public static RADIO_SIGNAL_SOUND: string = "featureAtomicwild";
    public static WAYS_UPDATE: LoopableList<string> = new LoopableList<string>(["waysUpdate01", "waysUpdate02", "waysUpdate03"]).setRandomizeAtWrapAround(true);
    private static _prevReelStopSound: string = "";
    private static _prevBomberExplodeSound: string = "";
    private static _davidPanningReelStop: number[] = [-0.14, -0.07, 0.0, 0.07, 0.14];
    public static BIG_WIN_SOUND: string[] = ["bigwin1", "bigwin2", "bigwin3", "bigwin4", "bigwin5"];

    public static get soundPlayer(): NolimitAudioPlayer {
        return <NolimitAudioPlayer>SlotGame.sound;
    }

    constructor() {
    }

    private static _currentMusic: string;

    public static switchMusic(conf: IPlayTrackConf, fadeInConf?: IFadeTrackConf, fadeOutConf?: IFadeTrackConf): void {
        if (BlitzKriegSound._currentMusic != null) {
            if (fadeOutConf) {
                fadeOutConf.name = this._currentMusic;
                const fade = BlitzKriegSound.fadeTrack(fadeOutConf);
                const fadeName = this._currentMusic;
                if (fade) {
                    gsap.to(this, {
                        duration: fadeOutConf.duration / 1000,
                        onComplete: () => {
                            BlitzKriegSound.stopTrack(fadeName);
                        }
                    });
                } else {
                    BlitzKriegSound.stopTrack(fadeName);
                }
            } else {
                BlitzKriegSound.stopTrack(BlitzKriegSound._currentMusic);
            }
        }

        BlitzKriegSound.playTrack(conf);
        if (fadeInConf) {
            BlitzKriegSound.fadeTrack(fadeInConf);
        }
        BlitzKriegSound._currentMusic = conf.name;
    }

    public static playTrack(conf: IPlayTrackConf) {
        console.log("Sound: Play Track: ", conf.name);
        BlitzKriegSound.soundPlayer.playTrack(conf);
    }

    public static playTrackWithLoop(conf: IPlayTrackConf) {
        console.log("Sound: Play Track: ", conf.name);
        BlitzKriegSound.soundPlayer.playTrack(conf)?.setLoop(true);
    }

    public static setCurrentMusicName(name: string): void {
        BlitzKriegSound._currentMusic = name;
    }

    public static stopTrack(name: string) {
        console.log("Sound: Stop Track: ", name);
        BlitzKriegSound.soundPlayer.stopTrack(name);
    }

    public static playRandomReelStopSound(reelId: number): void {
        let soundName: string = BlitzKriegSound.REELS_LANDING.next();
        this.playTrackWithDavidPanning(soundName, reelId);
    }

    public static playTrackWithDavidPanning(soundName: string, reelId: number) {
        while (soundName === this._prevReelStopSound) {
            soundName = BlitzKriegSound.REELS_LANDING.next();
        }
        this._prevReelStopSound = soundName;
        this.playTrackUsingDavidPanning(soundName, reelId);
    }

    public static playBomberExplodeWithDavidPanning(soundName: string, reelId: number) {
        while (soundName === this._prevBomberExplodeSound) {
            soundName = BlitzKriegSound.BOMBER_WILD_EXPLODE.next();
        }
        this._prevBomberExplodeSound = soundName;
        this.playTrackUsingDavidPanning(soundName, reelId);
    }

    public static playTrackUsingDavidPanning(soundName: string, reelId: number) {
        const sound: NolimitHowl | undefined = this.soundPlayer.playTrack({
            name: soundName
        });
        Logger.logDev("Sound: Play Track: ", soundName);

        if (sound) {
            sound.player.stereo(BlitzKriegSound._davidPanningReelStop[reelId]);
        }
    }

    public static fadeTrack(conf: IFadeTrackConf): NolimitHowl | undefined {
        console.log("Sound: Fade Track: ", conf.name);
        return BlitzKriegSound.soundPlayer.fadeTrack(conf);
    }

    // Use this to handle the music, eg, fade out/in music in big win, and you don't need to care what music is playing
    public static fadeMusic(vol: number, duration: number): void {
        console.log("Sound: Fade Track: ", BlitzKriegSound._currentMusic, " ", "Vol: ", vol);
        BlitzKriegSound.soundPlayer.fadeTrack({
            name: BlitzKriegSound._currentMusic,
            to: vol,
            duration: duration
        });
    }

    public static getMusicNameByMode(mode: GameMode | string): string {
        switch (mode) {
            case GameMode.FREESPIN:
                // return BlitzKriegSound.FREESPIN;
                return BlitzKriegSound.FREESPIN_MUSIC; // FREE SPIN file required
            default:
                return BlitzKriegSound.MAIN_GAME_MUSIC;
        }
    }

    public static fadeAndStopSound(fadeOutConf: IFadeTrackConf): void {
        const fade = BlitzKriegSound.soundPlayer.fadeTrack(fadeOutConf);
        if (fade) {
            gsap.to(this, {
                duration: fadeOutConf.duration / 1000,
                onComplete: () => {
                    BlitzKriegSound.stopTrack(fadeOutConf.name);
                }
            });
        } else {
            BlitzKriegSound.stopTrack(fadeOutConf.name);
        }
    }
}
